// hooks/useCampaignData.js
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { mockFetch } from '../services/mockApiService';

// ─── Mock toggle ──────────────────────────────────────────────────────────────
// Set REACT_APP_USE_MOCK_API=true in your .env to use mock data.
// When false, all fetch() calls go to the real API as before.
const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';

// Unified fetch wrapper — real fetch or mock, same signature
const apiFetch = USE_MOCK ? mockFetch : fetch;

// Global cache object to persist data across component unmounts
const globalCache = {
  data: [],
  summaryStats: null,
  timestamp: null,
  error: null,
};

const CACHE_DURATION = 5 * 60 * 1000;

export const useCampaignData = () => {
  const isCacheValid = useCallback(() => {
    if (!globalCache.timestamp || !globalCache.data || globalCache.data.length === 0) {
      return false;
    }
    return Date.now() - globalCache.timestamp < CACHE_DURATION;
  }, []);

  const [data, setData] = useState(() => (isCacheValid() ? globalCache.data : []));
  const [loading, setLoading] = useState(() => !isCacheValid());
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(() => (isCacheValid() ? globalCache.error : null));
  const [summaryStats, setSummaryStats] = useState(() =>
    isCacheValid() ? globalCache.summaryStats : null,
  );
  const [initialized, setInitialized] = useState(() => isCacheValid());

  const hasInitialized = useRef(false);
  const abortControllerRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const API_KEY = process.env.REACT_APP_PIPELINE_API_KEY || 'dev-key-12345';

  // ─── Data mapper ────────────────────────────────────────────────────────────
  // Unchanged from original — only the fetch source changes above
  const mapBigQueryData = useCallback((apiData) => {
    if (!Array.isArray(apiData)) {
      console.error('❌ API response is not an array:', typeof apiData);
      throw new Error('API response is not an array');
    }

    const usedIds = new Set();
    const duplicateTracker = new Map();

    const mappedData = apiData
      .map((row, index) => {
        if (!row || typeof row !== 'object') {
          console.warn(`⚠️ Invalid row data at index ${index}:`, row);
          return null;
        }

        try {
          let baseId = row.event || row.id;
          let finalId;

          if (baseId) {
            baseId = String(baseId).trim();
            if (usedIds.has(baseId)) {
              const currentCount = duplicateTracker.get(baseId) || 1;
              duplicateTracker.set(baseId, currentCount + 1);
              console.warn(`🚨 Skipping duplicate ID "${baseId}" at index ${index}`);
              return null;
            } else {
              duplicateTracker.set(baseId, 1);
              usedIds.add(baseId);
              finalId = baseId;
            }
          } else {
            finalId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`;
            while (usedIds.has(finalId)) {
              finalId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`;
            }
            usedIds.add(finalId);
          }

          return {
            id: finalId,
            date: row.date || new Date().toISOString().split('T')[0],
            invitations: Number(row.total_invites) || 0,
            calls: {
              cold: Number(row.cold_calendar) || 0,
              linkedin_invites: Number(row.linkedin_invites) || 0,
              non_joiners: Number(row.non_joiners) || 0,
              expansion: Number(row.expandi) || 0,
              total: Number(row.total_calls) || Number(row.total_invites) || 0,
            },
            registrations: {
              total: Number(row.total_regs) || 0,
              coldCalls: Number(row.cal_yes) || 0,
              maybe: Number(row.cal_maybe) || 0,
              linkedin: Number(row.li_event) || 0,
              zoom: Number(row.zoom_regs) || 0,
              regPercent:
                row.total_invites > 0
                  ? ((Number(row.total_regs) / Number(row.total_invites)) * 100).toFixed(1)
                  : '0',
            },
            attendees: {
              total: Number(row.total_attendees) || 0,
              coldCalls: Number(row.coldcal_attendees) || 0,
              non_joiners: Number(row.nonjoiners_attendees) || 0,
              linkedin: Number(row.linkedin_attendees) || 0,
              other: Number(row.other_attendees) || 0,
              attnToReg:
                row.total_regs > 0
                  ? ((Number(row.total_attendees) / Number(row.total_regs)) * 100).toFixed(1)
                  : '0',
              attnToInv:
                row.total_invites > 0
                  ? ((Number(row.total_attendees) / Number(row.total_invites)) * 100).toFixed(1)
                  : '0',
              uniqueViewers: Number(row.unique_viewers) || 0,
            },
            bookings: {
              total: Number(row.book_call_tot) || 0,
              webinar: Number(row.booked_call_web) || 0,
              site: Number(row.book_call_site) || 0,
              email: Number(row.book_call_sms) || 0,
              set: Number(row.book_call_set) || 0,
              other: Number(row.book_call_other) || 0,
              bookToAtt:
                row.total_attendees > 0
                  ? ((Number(row.book_call_tot) / Number(row.total_attendees)) * 100).toFixed(1)
                  : '0',
              bookToInv:
                row.total_invites > 0
                  ? ((Number(row.book_call_tot) / Number(row.total_invites)) * 100).toFixed(2)
                  : '0',
            },
            sales: {
              total: Number(row.sales) || 0,
              show: Number(row.show) || 0,
              showPercent:
                row.book_call_tot > 0
                  ? ((Number(row.show) / Number(row.book_call_tot)) * 100).toFixed(1)
                  : '0',
              noShow: Number(row.no_show) || 0,
              qualified: Number(row.qualified) || 0,
              conversions: Number(row.sales) || 0,
              revenue: Number(row.revenue) || 0,
              salesPercent:
                row.show > 0 ? ((Number(row.sales) / Number(row.show)) * 100).toFixed(1) : '0',
            },
            conversions: {
              inviteToReg:
                row.total_invites > 0
                  ? ((Number(row.total_regs) / Number(row.total_invites)) * 100).toFixed(1)
                  : '0',
              regToAttend:
                row.total_regs > 0
                  ? ((Number(row.total_attendees) / Number(row.total_regs)) * 100).toFixed(1)
                  : '0',
              attendToBook:
                row.total_attendees > 0
                  ? ((Number(row.book_call_tot) / Number(row.total_attendees)) * 100).toFixed(1)
                  : '0',
              bookedToShow:
                row.book_call_tot > 0
                  ? ((Number(row.show) / Number(row.book_call_tot)) * 100).toFixed(1)
                  : '0',
              showToSale:
                row.show > 0 ? ((Number(row.sales) / Number(row.show)) * 100).toFixed(1) : '0',
            },
            trends: {
              invites: 0.0,
              registrations: 0,
              attendees: 0,
              bookings: 0,
              callsShow: '0',
              sales: '0',
            },
          };
        } catch (mappingError) {
          console.error(`❌ Error mapping row at index ${index}:`, mappingError, row);
          return null;
        }
      })
      .filter(Boolean);

    const duplicates = Array.from(duplicateTracker.entries()).filter(([, count]) => count > 1);
    if (duplicates.length > 0) {
      console.warn(
        `🚨 DUPLICATE SUMMARY:`,
        duplicates.map(([id, count]) => `${id} (${count}x)`).join(', '),
      );
    }

    return mappedData;
  }, []);

  // ─── Fetch campaigns ─────────────────────────────────────────────────────────
  const fetchCampaigns = useCallback(
    async (filters = {}, isRefresh = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          include_trends: 'false',
          format: 'bigquery',
          ...filters,
        });

        const response = await apiFetch(
          `${API_BASE_URL}/analytics/campaigns?${queryParams}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${API_KEY}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            signal,
          },
        );

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const apiData = await response.json();
        const mappedData = mapBigQueryData(Array.isArray(apiData) ? apiData : apiData.data || []);

        setData(mappedData);
        globalCache.data = mappedData;
        globalCache.timestamp = Date.now();
        globalCache.error = null;
      } catch (err) {
        if (err.name === 'AbortError') return;

        console.error('💥 Fetch error:', err);
        const errorMessage = `Failed to fetch campaigns: ${err.message}`;
        setError(errorMessage);
        globalCache.error = errorMessage;

        if (!isRefresh) {
          setData([]);
          globalCache.data = [];
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
        setInitialized(true);
        hasInitialized.current = true;
      }
    },
    [API_BASE_URL, API_KEY, mapBigQueryData],
  );

  // ─── Fetch summary ───────────────────────────────────────────────────────────
  const fetchSummary = useCallback(
    async (isRefresh = false) => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/analytics/summary`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const summary = await response.json();
          const summaryData = {
            totalCampaigns: Number(summary.total_campaigns) || 0,
            totalInvitations: Number(summary.total_invites) || 0,
            totalRegistrations: Number(summary.total_registrations) || 0,
            totalAttendees: Number(summary.total_attendees) || 0,
            totalBookings: Number(summary.total_bookings) || 0,
            totalSales: Number(summary.total_sales) || 0,
            totalRevenue: Number(summary.total_revenue) || 0,
            avgInviteToReg: Number(summary.avg_invite_to_reg_rate) || 0,
            avgRegToAttend: Number(summary.avg_reg_to_attend_rate) || 0,
            avgAttendToBook: Number(summary.avg_attend_to_book_rate) || 0,
          };
          setSummaryStats(summaryData);
          globalCache.summaryStats = summaryData;
        } else {
          console.warn('⚠️ Summary API failed, will calculate from campaign data');
        }
      } catch (err) {
        console.error('❌ Failed to fetch summary:', err);
      }
    },
    [API_BASE_URL, API_KEY],
  );

  // ─── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasInitialized.current && (!data || data.length === 0 || !isCacheValid())) {
      fetchCampaigns({}, false);
      fetchSummary(false);
    } else if (data.length > 0) {
      setInitialized(true);
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data && data.length > 0 && !summaryStats) {
      const totals = data.reduce(
        (acc, item) => ({
          totalCampaigns: acc.totalCampaigns + 1,
          totalInvitations: acc.totalInvitations + (item.invitations || 0),
          totalRegistrations: acc.totalRegistrations + (item.registrations?.total || 0),
          totalAttendees: acc.totalAttendees + (item.attendees?.total || 0),
          totalBookings: acc.totalBookings + (item.bookings?.total || 0),
          totalSales: acc.totalSales + (item.sales?.total || 0),
          totalRevenue: acc.totalRevenue + (item.sales?.revenue || 0),
        }),
        {
          totalCampaigns: 0,
          totalInvitations: 0,
          totalRegistrations: 0,
          totalAttendees: 0,
          totalBookings: 0,
          totalSales: 0,
          totalRevenue: 0,
        },
      );

      const calculatedSummary = {
        ...totals,
        avgInviteToReg:
          totals.totalInvitations > 0
            ? Number(((totals.totalRegistrations / totals.totalInvitations) * 100).toFixed(1))
            : 0,
        avgRegToAttend:
          totals.totalRegistrations > 0
            ? Number(((totals.totalAttendees / totals.totalRegistrations) * 100).toFixed(1))
            : 0,
        avgAttendToBook:
          totals.totalAttendees > 0
            ? Number(((totals.totalBookings / totals.totalAttendees) * 100).toFixed(1))
            : 0,
      };

      setSummaryStats(calculatedSummary);
      globalCache.summaryStats = calculatedSummary;
    }
  }, [data, summaryStats]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ─── Refetch ─────────────────────────────────────────────────────────────────
  const refetch = useCallback(() => {
    globalCache.data = [];
    globalCache.summaryStats = null;
    globalCache.timestamp = null;
    globalCache.error = null;
    setData([]);
    setSummaryStats(null);
    setError(null);
    hasInitialized.current = false;
    fetchCampaigns({}, true);
    fetchSummary(true);
  }, [fetchCampaigns, fetchSummary]);

  // ─── Cleanup ─────────────────────────────────────────────────────────────────
  const filteredData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const stableFetchCampaigns = useCallback(
    (filters) => fetchCampaigns(filters, true),
    [fetchCampaigns],
  );
  const stableFetchSummary = useCallback(() => fetchSummary(true), [fetchSummary]);

  const computedValues = useMemo(
    () => ({
      isCacheValid: isCacheValid(),
      cacheAge: globalCache.timestamp ? Date.now() - globalCache.timestamp : 0,
      debugInfo: {
        hasInitialized: hasInitialized.current,
        cacheTimestamp: globalCache.timestamp,
        dataLength: data.length,
        hasAbortController: !!abortControllerRef.current,
        usingMock: USE_MOCK,
      },
    }),
    [isCacheValid, data.length],
  );

  return useMemo(
    () => ({
      data: filteredData,
      loading,
      refreshing,
      error,
      summaryStats,
      initialized,
      lastUpdated: globalCache.timestamp,
      fetchCampaigns: stableFetchCampaigns,
      fetchSummary: stableFetchSummary,
      refetch,
      filteredData,
      API_BASE_URL,
      ...computedValues,
    }),
    [
      filteredData,
      loading,
      refreshing,
      error,
      summaryStats,
      initialized,
      stableFetchCampaigns,
      stableFetchSummary,
      refetch,
      API_BASE_URL,
      computedValues,
    ],
  );
};